import { useState, useEffect, useCallback, useRef } from 'react';
import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { Activity, ChevronUp, X } from 'lucide-react';

type Rating = 'good' | 'needs-improvement' | 'poor' | 'pending';

interface VitalEntry {
  name: string;
  value: number;
  rating: Rating;
  unit: string;
}

const thresholds: Record<string, { good: number; poor: number; unit: string; decimals: number }> = {
  LCP: { good: 2500, poor: 4000, unit: 's', decimals: 2 },
  CLS: { good: 0.1, poor: 0.25, unit: '', decimals: 3 },
  FCP: { good: 1800, poor: 3000, unit: 's', decimals: 2 },
  INP: { good: 200, poor: 500, unit: 'ms', decimals: 0 },
  TTFB: { good: 800, poor: 1800, unit: 'ms', decimals: 0 },
};

function getRating(name: string, value: number): Rating {
  const t = thresholds[name];
  if (!t) return 'pending';
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needs-improvement';
  return 'poor';
}

function formatValue(name: string, value: number): string {
  const t = thresholds[name];
  if (!t) return String(value);
  const formatted = value.toFixed(t.decimals);
  if (t.unit === 's') return `${(value / 1000).toFixed(t.decimals)}s`;
  return `${formatted}${t.unit}`;
}

const ratingColors: Record<Rating, string> = {
  good: 'bg-emerald-500 text-white',
  'needs-improvement': 'bg-amber-500 text-white',
  poor: 'bg-red-500 text-white',
  pending: 'bg-slate-400 text-white',
};

const ratingDot: Record<Rating, string> = {
  good: 'bg-emerald-500',
  'needs-improvement': 'bg-amber-500',
  poor: 'bg-red-500',
  pending: 'bg-slate-400',
};

const STORAGE_KEY = 'a1-performance-panel-collapsed';

const PerformancePanel = () => {
  const [vitals, setVitals] = useState<Record<string, VitalEntry>>({});
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [hidden, setHidden] = useState(false);
  const hasListeners = useRef(false);

  const registerVital = useCallback((metric: Metric) => {
    setVitals((prev) => {
      if (prev[metric.name]?.value === metric.value) return prev;
      return {
        ...prev,
        [metric.name]: {
          name: metric.name,
          value: metric.value,
          rating: getRating(metric.name, metric.value),
          unit: thresholds[metric.name]?.unit || '',
        },
      };
    });
  }, []);

  useEffect(() => {
    if (hasListeners.current) return;
    hasListeners.current = true;
    onCLS(registerVital);
    onINP(registerVital);
    onFCP(registerVital);
    onLCP(registerVital);
    onTTFB(registerVital);
  }, [registerVital]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  if (hidden) return null;

  const vitalList = Object.values(vitals);
  const overallRating: Rating =
    vitalList.length === 0
      ? 'pending'
      : vitalList.some((v) => v.rating === 'poor')
        ? 'poor'
        : vitalList.some((v) => v.rating === 'needs-improvement')
          ? 'needs-improvement'
          : 'good';

  return (
    <div className="fixed bottom-4 right-4 z-[9999] font-sans">
      {collapsed ? (
        <button
          onClick={toggleCollapsed}
          className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border border-white/20 backdrop-blur-md transition-transform hover:scale-105 ${ratingColors[overallRating]}`}
          title="Open Web Vitals panel"
        >
          <Activity className="w-4 h-4" />
          <span className="text-xs font-semibold uppercase tracking-wide">Web Vitals</span>
          <span
            className={`w-2 h-2 rounded-full animate-pulse ${overallRating === 'pending' ? 'bg-white/70' : 'bg-white'}`}
          />
        </button>
      ) : (
        <div className="w-72 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-slate-600 dark:text-slate-300" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Performance
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleCollapsed}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors"
                title="Collapse"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => setHidden(true)}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors"
                title="Hide"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Metrics */}
          <div className="px-4 py-3 space-y-2">
            {(['LCP', 'CLS', 'FCP', 'INP', 'TTFB'] as const).map((key) => {
              const vital = vitals[key];
              const rating = vital?.rating ?? 'pending';
              return (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${ratingDot[rating]}`} />
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                      {key}
                    </span>
                  </div>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${ratingColors[rating]}`}
                  >
                    {vital ? formatValue(key, vital.value) : '—'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
            <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center">
              Scroll the gallery to verify lazy-loading & prefetching
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformancePanel;
